import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { AlertCircle, CheckCircle, Clock, XCircle, Calendar } from "lucide-react";

export default function ECDashboard() {
  const { user, isAuthenticated, loading } = useAuth();
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [actionNotes, setActionNotes] = useState<string>("");
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "decline">("approve");

  // Fetch pending appointments
  const { data: pendingAppointments = [], isLoading: pendingLoading, refetch: refetchPending } =
    trpc.appointments.getPendingAppointments.useQuery();

  // Fetch all appointments for this EC member
  const { data: allAppointments = [], isLoading: allLoading, refetch: refetchAll } =
    trpc.appointments.getECMemberAppointments.useQuery();

  // Approve appointment mutation
  const approveAppointmentMutation = trpc.appointments.approveAppointment.useMutation({
    onSuccess: () => {
      refetchPending();
      refetchAll();
      setShowActionDialog(false);
      setActionNotes("");
    },
    onError: (error) => {
      console.error("Error approving appointment:", error);
    },
  });

  // Decline appointment mutation
  const declineAppointmentMutation = trpc.appointments.declineAppointment.useMutation({
    onSuccess: () => {
      refetchPending();
      refetchAll();
      setShowActionDialog(false);
      setActionNotes("");
    },
    onError: (error) => {
      console.error("Error declining appointment:", error);
    },
  });

  const handleApprove = (appointment: any) => {
    setSelectedAppointment(appointment);
    setActionType("approve");
    setActionNotes("");
    setShowActionDialog(true);
  };

  const handleDecline = (appointment: any) => {
    setSelectedAppointment(appointment);
    setActionType("decline");
    setActionNotes("");
    setShowActionDialog(true);
  };

  const handleSubmitAction = () => {
    if (!selectedAppointment) return;

    if (actionType === "approve") {
      approveAppointmentMutation.mutate({
        appointmentId: selectedAppointment.id,
        notes: actionNotes || undefined,
      });
    } else {
      if (!actionNotes) {
        alert("Please provide a reason for declining");
        return;
      }
      declineAppointmentMutation.mutate({
        appointmentId: selectedAppointment.id,
        notes: actionNotes,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-800">Pending</Badge>;
      case "approved":
        return <Badge variant="outline" className="bg-green-50 text-green-800">Approved</Badge>;
      case "declined":
        return <Badge variant="outline" className="bg-red-50 text-red-800">Declined</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-blue-50 text-blue-800">Completed</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-gray-50 text-gray-800">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const AppointmentCard = ({ appointment }: { appointment: any }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{appointment.subject}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Resident: {appointment.residentName || "Unknown"}
              </p>
            </div>
            {getStatusBadge(appointment.status)}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Date</span>
              <p className="font-medium">{format(parseISO(appointment.appointmentDate), "MMM d, yyyy")}</p>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Time</span>
              <p className="font-medium">
                {appointment.startTime} - {appointment.endTime}
              </p>
            </div>
          </div>

          {appointment.description && (
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Description</span>
              <p className="text-sm mt-1">{appointment.description}</p>
            </div>
          )}

          {appointment.notes && (
            <div className="bg-muted p-3 rounded">
              <span className="text-sm font-medium">Notes</span>
              <p className="text-sm mt-1">{appointment.notes}</p>
            </div>
          )}

          {appointment.status === "pending" && (
            <div className="flex gap-2 pt-2">
              <Button
                size="sm"
                variant="default"
                onClick={() => handleApprove(appointment)}
                disabled={approveAppointmentMutation.isPending}
                className="flex-1"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDecline(appointment)}
                disabled={declineAppointmentMutation.isPending}
                className="flex-1"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Decline
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2">
            <CardHeader className="text-center">
              <CardTitle>EC Member Dashboard</CardTitle>
              <CardDescription>Please log in to manage appointments</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">You need to be logged in to access this dashboard.</p>
              <Button size="lg">Log In</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">EC Member Dashboard</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Manage appointment requests from residents
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pending Requests</p>
                  <p className="text-3xl font-bold">{pendingAppointments.length}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Approved</p>
                  <p className="text-3xl font-bold">
                    {allAppointments.filter((a: any) => a.status === "approved").length}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Appointments</p>
                  <p className="text-3xl font-bold">{allAppointments.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appointments Tabs */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending">
              Pending ({pendingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({allAppointments.filter((a: any) => a.status === "approved").length})
            </TabsTrigger>
            <TabsTrigger value="all">All ({allAppointments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4 mt-6">
            {pendingLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-gray-600">Loading pending appointments...</p>
              </div>
            ) : pendingAppointments.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600">No pending appointment requests</p>
                </CardContent>
              </Card>
            ) : (
              pendingAppointments.map((appointment: any) => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4 mt-6">
            {allLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-gray-600">Loading approved appointments...</p>
              </div>
            ) : (
              (() => {
                const approved = allAppointments.filter((a: any) => a.status === "approved");
                return approved.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="text-gray-600">No approved appointments</p>
                    </CardContent>
                  </Card>
                ) : (
                  approved.map((appointment: any) => (
                    <AppointmentCard key={appointment.id} appointment={appointment} />
                  ))
                );
              })()
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4 mt-6">
            {allLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-gray-600">Loading all appointments...</p>
              </div>
            ) : allAppointments.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-gray-600">No appointments yet</p>
                </CardContent>
              </Card>
            ) : (
              allAppointments.map((appointment: any) => (
                <AppointmentCard key={appointment.id} appointment={appointment} />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Appointment" : "Decline Appointment"}
            </DialogTitle>
            <DialogDescription>
              {selectedAppointment?.subject} - {selectedAppointment?.appointmentDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="action-notes">
                {actionType === "approve" ? "Notes (Optional)" : "Reason for Declining *"}
              </Label>
              <Textarea
                id="action-notes"
                placeholder={
                  actionType === "approve"
                    ? "Add any notes for the resident..."
                    : "Please provide a reason for declining this appointment"
                }
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowActionDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAction}
                disabled={
                  actionType === "decline" && !actionNotes
                    ? true
                    : approveAppointmentMutation.isPending || declineAppointmentMutation.isPending
                }
                className={actionType === "approve" ? "" : "bg-red-600 hover:bg-red-700"}
              >
                {actionType === "approve" ? "Approve" : "Decline"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
